"use client";
import React, { useState } from "react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { useMediaQuery } from "@mui/material";
import AnimatedImage from "@/main/_components/animated-image";
import { languageIds } from "@/lib/language";
import SliderText from "@/main/_components/slider-text";
import SliderButton from "@/main/_components/slider-button";
import "../../layerslider.css";
import { HomepageSliderProps } from "@/types/layerslider";
import Image from "next/image";

const SwiperButtonNext = () => {
  const swiper = useSwiper();
  return (
    <div
      id="nextButtonHomepage"
      style={{
        display: "block",
        width: "30px",
        height: "40px",
        lineHeight: "70px",
        textAlign: "center",
        cursor: "pointer",
        top: "50%",
        marginTop: "-35px",
        position: "absolute",
        zIndex: 1,
        color: "#ffffff",
        fontSize: "36px",
        borderRadius: "50%",
        fontFamily: "FontAwesome",
        right: "30px",
      }}
      onClick={() => swiper.slideNext()}
    >
      <span>
        <svg
          fill="#ffffff"
          width="30px"
          height="40px"
          viewBox="0 0 52 52"
          data-name="Layer 1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier">
            <g data-name="Group 132" id="Group_132">
              <path d="M14,52a2,2,0,0,1-1.41-3.41L35.17,26,12.59,3.41a2,2,0,0,1,0-2.82,2,2,0,0,1,2.82,0l24,24a2,2,0,0,1,0,2.82l-24,24A2,2,0,0,1,14,52Z"></path>
            </g>
          </g>
        </svg>
      </span>
    </div>
  );
};

const SwiperButtonPrev = () => {
  const swiper = useSwiper();
  return (
    <div
      id="prevButtonHomepage"
      style={{
        display: "block",
        width: "30px",
        height: "40px",
        lineHeight: "70px",
        textAlign: "center",
        cursor: "pointer",
        top: "50%",
        left: "30px",
        marginTop: "-35px",
        position: "absolute",
        zIndex: 1,
        color: "#ffffff",
        fontSize: "36px",
        borderRadius: "50%",
        fontFamily: "FontAwesome",
      }}
      onClick={() => swiper.slidePrev()}
    >
      <span>
        <svg
          fill="#ffffff"
          width="30px"
          height="40px"
          viewBox="0 0 52 52"
          data-name="Layer 1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier">
            <g data-name="Group 132" id="Group_132">
              <path d="M38,52a2,2,0,0,1-1.41-.59l-24-24a2,2,0,0,1,0-2.82l24-24a2,2,0,0,1,2.82,0,2,2,0,0,1,0,2.82L16.83,26,39.41,48.59A2,2,0,0,1,38,52Z"></path>{" "}
            </g>
          </g>
        </svg>
      </span>
    </div>
  );
};

const HomepageSlider: React.FC<HomepageSliderProps> = ({ item, lang }) => {
  const isMobile = useMediaQuery("(max-width:767.5px)");

  const sliderStyles: React.CSSProperties = {
    width: item.fullwidth === "1" ? "100%" : isMobile ? "100%" : `${item.width}px`,
    height: isMobile ? "auto" : `${item.height}px`,
    aspectRatio: item.width && item.height ? `${item.width} / ${item.height}` : "16/9",
    minHeight: `${item.minheight}px`,
    overflow: "hidden",
  };
  const paginationConfig = {
    clickable: true,
    dynamicBullets: false,
    enabled: true,
    renderBullet: (index: number, className: string) => {
      const isActive = className.includes("swiper-pagination-bullet-active");
      const activeId = isActive ? null : "homepage-pagination-bullet-active";

      return `
      <span id=${activeId} class="${className}" style="
        width: 12px;
        height: 12px;
        border: 2px solid #ffffff;
        border-radius: 50%;
        margin: 0 3px !important; 
        transition: background 400ms; 
        background: transparent; 
        box-sizing: content-box;
      "></span>`;
    },
    containerClass: "swiper-pagination-bullets",
  };
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div
      className="responsive-margin"
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: item.fullwidth === "1" ? "100%" : `${item.width}px`,
        overflow: "hidden",
        margin: 0,
        marginTop: item.margin_top || "0px",
        marginBottom: item.margin_bottom || "0px",
      }}
    >
      <Swiper
        id="homepage-carousel"
        loop={item.loop === "1"}
        spaceBetween={10}
        pagination={paginationConfig}
        navigation={{
          nextEl: ".swiper-button-next #nextButtonHomepage",
          prevEl: ".swiper-button-prev #prevButtonHomepage",
        }}
        autoplay={{
          delay: 10000,
          disableOnInteraction: false,
        }}
        speed={800}
        modules={[Autoplay, Pagination, Navigation]}
        className="carousel"
        style={sliderStyles}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
      >
        <SwiperButtonPrev />

        {Object.values(item.sections || {}).map((section, sectionIndex) => {
          const hasLink = section?.link && section.link.trim() !== "";
          const isNewWindow = section?.link_new_window === "1";
          const isActive = activeIndex === sectionIndex;
          return (
            <SwiperSlide
              key={sectionIndex}
              style={{
                backgroundColor: section.bg_color || "transparent",
                cursor: hasLink ? "pointer" : "default",
              }}
              onClick={() => {
                if (hasLink) {
                  if (isNewWindow) {
                    window.open(section.link, "_blank", "noopener,noreferrer");
                  } else {
                    window.location.href = section.link;
                  }
                }
              }}
            >
              {/* Wrap the image and overlay in a relative container */}
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                {section.thumb_image && isActive && (
                  <Image
                    src={`${process.env.NEXT_PUBLIC_BASE_URL}/image/${section.thumb_image}`}
                    alt="Slider Image"
                    fill
                    style={{ objectFit: "cover" }}
                    fetchPriority="high"
                    priority
                    sizes="100vw"
                  />
                )}
                <div className="relative flex h-[-webkit-fill-available] w-full items-center justify-center md:h-[600px]">
                  <div className="relative flex h-[-webkit-fill-available] w-[900px] items-center justify-center md:h-[600px]">
                    {Object.values(section.groups || {}).map((group, groupIndex) => {
                      switch (group.type) {
                        case "image":
                          return (
                            <React.Fragment key={groupIndex}>
                              {isActive && (
                                <AnimatedImage
                                  group={group}
                                  value={group.image[languageIds[lang]]}
                                  lang={lang}
                                />
                              )}
                            </React.Fragment>
                          );
                        case "button":
                          return (
                            <React.Fragment key={groupIndex}>
                              <SliderButton group={group} lang={languageIds[lang]} />
                            </React.Fragment>
                          );
                        case "text":
                          return (
                            <React.Fragment key={groupIndex}>
                              <SliderText group={group} lang={languageIds[lang]} />
                            </React.Fragment>
                          );
                        default:
                          return null;
                      }
                    })}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}

        <SwiperButtonNext />
      </Swiper>
    </div>
  );
};

export default React.memo(HomepageSlider);
